import type { INavGate } from '@/libs/navPermission'
import { mayOpenGate } from '@/libs/navPermission'

/**
 * The menu of this console, in menu order, and the ONE list three things are built from:
 * the sidebar, the landing rule and the permission sweep in `tools/landing.test.mjs`.
 *
 * # Why this file exists
 *
 * It replaces a sidebar that carried its own copy of the order and a landing rule that
 * carried none at all. `firstAllowed()` used to walk `router.getRoutes()` and hand back
 * the first route the account could open — and that list is neither the menu's order nor
 * a list of screens. It holds the DETAIL routes too, so an account holding only
 * `group:read` was redirected onto `/porocila/:id/teki/:runId`, vue-router refused to
 * build an address with no `id` in hand („Missing required param“), and the account sat
 * on a redirect it could not leave. One holding only `group:sales` was sent to
 * `/skupine/:id` the same way. It was found by a reviewer with a one-right account, not
 * by anybody using the console, because every account in the house holds `group:report`
 * and lands on „Pregled“.
 *
 * Patching the search would have fixed those two routes. Naming the menu once fixes the
 * family: a detail route is not IN this list, so it can never be landed on, and the day
 * somebody adds `/pogodbe/:id` there is nothing to remember.
 *
 * # What an entry is, and what it is not
 *
 * An entry is a SCREEN OF THE MENU. Three kinds of route are deliberately absent:
 *
 *   - the detail routes (`master-group`, `report-runs`, `report-run`) — they are reached
 *     from a row or from a link in a mail, they carry a parameter, and nothing may send
 *     an account to one without the record it is about;
 *   - the sign-in, which is not behind a session at all;
 *   - „Brez dostopa“, which is what the landing answers WHEN THIS LIST ANSWERS NOTHING.
 *     It declares no permission, so a search that included it would find it first for
 *     everybody. That used to be held off by `meta.landing: false` on the route; the flag
 *     is gone, because „not in the menu“ already says it and a second way to say it is a
 *     second thing to keep true.
 *
 * `NOT_IN_MENU` below names every authenticated route that is not here, with its reason,
 * and the test fails on any route that is in neither list. A screen added tomorrow is
 * therefore either in the menu or explained.
 *
 * # The order is the order of the work
 *
 * It is the mockup's and it is argued screen by screen in `router/index.ts` and in
 * `docs/UPRAVA-KONZOLA.md`: what the group did yesterday, what waits, whether it is still
 * selling, what it has sold, what that cost, what is left of it — and then the group's
 * own bookkeeping. The landing walks it in exactly this order, so where an account may
 * open several screens it lands on the first one its morning starts with, rather than on
 * whichever route the bundler happened to register first.
 *
 * # The right is declared here and in `meta`, and the two are checked against each other
 *
 * The router's `meta` stays authoritative for the GUARD — it has to, because a typed
 * address reaches the guard without passing a menu, and because the service's own
 * `HTTP_API/ConsolePermissions_test.go` reads those `meta` blocks out of `router/index.ts`
 * and holds each screen to the rights its first reads need. This list declares the same
 * gate for the MENU and the LANDING, and `tools/landing.test.mjs` fails the moment the
 * two disagree. Two declarations that are mechanically held equal are not two sources of
 * truth; two that nobody compares are.
 */

/** A screen of the menu: what it is called, what it is drawn as, and what it needs. */
export interface INavEntry extends INavGate {
    /** The route name — the handle the sidebar, the landing and the test all use. */
    name:     string
    /** The Lucide icon the sidebar draws. The house rule is that no two entries share
     *  one — two rows under one icon is a menu somebody misclicks every morning — and
     *  „Skupine“ and „Ljudje“ have both carried `i-lucide-users` since before this file
     *  and are left alone here, because a landing fix is not where an icon changes under
     *  somebody. It is NOT asserted for that reason. */
    icon:     string
    /** The i18n key of the label. A key and not a string, because this file is DATA and
     *  a module that reached for `useI18n()` could only be read inside a component. */
    labelKey: string
}

/** A run of entries under one quiet heading. A section with no label draws none. */
export interface INavSection {
    /** The i18n key of the heading, or nothing for the first section. */
    labelKey?: string
    items:     INavEntry[]
}

/**
 * The menu, in two sections.
 *
 * # Why there are two
 *
 * The first section is the WORK — what the group did yesterday, what is waiting, what is
 * on the books, what it cost. The second is the group's own bookkeeping: who the
 * companies are, who reports to whom, what a dinar was worth in September, what the
 * console has been asked to do. The first is opened every morning and the second when
 * something has changed, and a single list of twenty-six would put „Struktura“ between
 * them.
 *
 * The split is the mockup's, and the mockup leaves room in the middle for the sections
 * G2 onwards bring (Prodaja, Nabava in finance, Kadri in marketing). They are not drawn
 * here: a heading over nothing is a promise, and the day one of them has a section it
 * arrives with it.
 */
export const NAV_SECTIONS: INavSection[] = [
    {
        items: [
            { name: 'overview', icon: 'i-lucide-layout-dashboard', labelKey: 'nav.overview', permission: 'group:report' },
            { name: 'alerts',   icon: 'i-lucide-bell-ring',        labelKey: 'nav.alerts',   permission: 'group:read' },

            // After „Opozorila“ and before „Pickup“, which is the order the work is done
            // in: what is waiting, then whether the group is still selling, then what
            // that selling has already put on the books.
            { name: 'channels', icon: 'i-lucide-radio',       labelKey: 'nav.channels', permission: 'group:read' },
            { name: 'pickup',   icon: 'i-lucide-trending-up', labelKey: 'nav.pickup',   permission: 'group:report' },

            // After „Pickup“, because that is the order the question is asked in: what is
            // on the books, and then what the group is asking for it.
            { name: 'rate-templates', icon: 'i-lucide-tags', labelKey: 'nav.rate_templates', permission: 'group:sales' },

            // The group's corporate agreements (WP-G6), after „Ceniki“ because that is
            // the order the question is asked in: what is on the books, and then with
            // whom it was agreed.
            { name: 'contracts', icon: 'i-lucide-handshake', labelKey: 'nav.contracts', permission: 'group:sales' },

            // The central reservation service (WP-G7). It is the first entry of this menu
            // that leads to a screen which WRITES into a hotel, which is why it sits with
            // the selling rather than with the group's own bookkeeping below.
            { name: 'reservations', icon: 'i-lucide-bed-double', labelKey: 'nav.reservations', permission: 'group:sales' },

            // And the congress across several hotels (WP-G8), straight after
            // „Rezervacije“ because it is the same errand one size up: that screen sells
            // one room in one hotel, and this one signs a contract over four of them.
            //
            // The LIST and never `master-group`, its detail. That distinction is the
            // whole reason this file exists.
            { name: 'master-groups', icon: 'i-lucide-users', labelKey: 'nav.master_groups', permission: 'group:sales' },

            // Skupinske kampanje (WP-G20), after „Rezervacije“ and before „Proračun“:
            // that entry is where the group sells the next room, and this is where it
            // goes and asks for one. `group:marketing` and not `group:sales`, because §8
            // made marketing its own circle of trust precisely so that „sees the group“
            // does not imply „sees the mailing list“.
            { name: 'campaigns', icon: 'i-lucide-megaphone', labelKey: 'nav.campaigns', permission: 'group:marketing' },

            // What the advertising brought back (WP-G21): those are the rooms the group
            // sells, and this is what it spent to sell them.
            //
            // NOT `i-lucide-trending-up`, which „Pickup“ already carries.
            { name: 'marketing-roi', icon: 'i-lucide-chart-line', labelKey: 'nav.marketing_roi', permission: 'group:marketing' },

            // After „Pickup“, which is the order the question is asked in: what is on the
            // books, and then whether that is what the group said it would be.
            { name: 'budgets', icon: 'i-lucide-target', labelKey: 'nav.budgets', permission: 'group:report' },

            // And straight after „Proračun“, what the group KEPT of it (WP-G15): the same
            // right, and the order the question is asked in — what was promised, then
            // what came in, then what was left of it.
            { name: 'pnl', icon: 'i-lucide-scale', labelKey: 'nav.pnl', permission: 'group:report' },

            { name: 'materials', icon: 'i-lucide-shopping-cart', labelKey: 'nav.materials', permission: 'group:finance' },

            // Straight after „Nabava“, because it is the same errand one step on: that
            // screen says what the group BOUGHT and this one hands the papers to the
            // book-keeper. `allOf` and not one key: the screen's first read is the
            // register of companies its picker hangs off, and the service guards that
            // with `group:read` alone.
            {
                name:       'accounting',
                icon:       'i-lucide-book-open-check',
                labelKey:   'nav.accounting',
                permission: 'group:finance',
                allOf:      ['group:finance', 'group:read'],
            },

            // And after „Knjigovodstvo“, the document those books need where the group is
            // more than one company (WP-G12): what one company sold another when stock
            // crossed between their houses. `allOf` for the entry above's own reason —
            // `onMounted` reads the register of companies before anything else.
            {
                name:       'intercompany',
                icon:       'i-lucide-arrow-left-right',
                labelKey:   'nav.intercompany',
                permission: 'group:finance',
                allOf:      ['group:finance', 'group:read'],
            },
        ],
    },
    {
        labelKey: 'nav.group_section',
        items:    [
            { name: 'structure', icon: 'i-lucide-network',     labelKey: 'nav.structure', permission: 'group:read' },
            { name: 'entities',  icon: 'i-lucide-building-2',  labelKey: 'nav.entities',  permission: 'group:read' },
            { name: 'fx',        icon: 'i-lucide-coins',       labelKey: 'nav.fx',        permission: 'group:read' },

            // The group's own monthly close (WP-G13), after „Tečaji“ because that is the
            // order the work is done in: the rate is filed and then the month is declared
            // finished.
            { name: 'month-close', icon: 'i-lucide-lock',        labelKey: 'nav.month_close', permission: 'group:finance' },
            { name: 'operations',  icon: 'i-lucide-list-checks', labelKey: 'nav.operations',  permission: 'group:read' },
            { name: 'milo',        icon: 'i-lucide-sparkles',    labelKey: 'nav.milo',        permission: 'group:read' },

            // The LIST of reports. `report-runs` and `report-run` are its detail routes
            // and are not entries — see the head of this file.
            { name: 'reports', icon: 'i-lucide-newspaper', labelKey: 'nav.reports', permission: 'group:read' },

            // The register of people (WP-G16). At the END and not beside „Struktura“: it
            // is the first entry of this menu that a reader may not hold the right for.
            // When „Kadri in marketing“ arrives as a section of its own, this is the row
            // that moves into it, with the three below.
            { name: 'people', icon: 'i-lucide-users', labelKey: 'nav.people', permission: 'group:hr' },

            // And the payroll file (WP-G18), straight after the register it is built on:
            // the same right, the same circle of trust, and the order of the work — who
            // works here, then what leaves for the wage run. `allOf` for
            // „Knjigovodstvo“'s own reason: the file is assembled PER COMPANY, so there
            // is no sheet without the register, and that register is `group:read`.
            {
                name:       'payroll',
                icon:       'i-lucide-banknote',
                labelKey:   'nav.payroll',
                permission: 'group:hr',
                allOf:      ['group:hr', 'group:read'],
            },

            // And the loans (WP-G17), after the two it stands on: who works here, what
            // leaves for the wage run, and who of them is standing in another hotel this
            // fortnight.
            { name: 'loans', icon: 'i-lucide-arrow-right-left', labelKey: 'nav.loans', permission: 'group:hr' },

            // And what all three of them COST (WP-G19): the month's hours and what they
            // cost per sold night.
            { name: 'staff', icon: 'i-lucide-briefcase', labelKey: 'nav.staff', permission: 'group:hr' },
        ],
    },
]

/** The menu flattened into one list, in menu order. The landing walks exactly this. */
export const NAV_ENTRIES: INavEntry[] = NAV_SECTIONS.flatMap(section => section.items)

/**
 * Every authenticated route of this console that is NOT a menu entry, and why.
 *
 * It is written out rather than inferred so that the test can fail on a route in neither
 * list. „I forgot to put it in the menu“ and „it deliberately has no entry“ look
 * identical from the outside, and the difference is exactly what went wrong here: the old
 * landing treated every route as a candidate because nothing had ever said which of them
 * were screens.
 */
export const NOT_IN_MENU: { name: string, reason: string }[] = [
    {
        name:   'master-group',
        reason: 'Detail of „Skupine“: one congress, reached from a row or from a link in a mail. '
            + 'It carries :id and there is no congress to name when nobody asked for one.',
    },
    {
        name:   'report-runs',
        reason: 'Detail of „Poročila“: the teki of one report. Carries :id.',
    },
    {
        name:   'report-run',
        reason: 'Detail of „Poročila“: one tek, read-only. Carries :id and :runId — this is the '
            + 'route the old landing search sent a `group:read` account to.',
    },
    {
        name:   'no-access',
        reason: 'The sentence an account with no group right at all is shown. It declares no '
            + 'permission, so it must never be FOUND by the landing search — it is what the '
            + 'search answers when it finds nothing.',
    },
]

/**
 * Whether a path may be landed on at all — the mechanical half of the rule.
 *
 * A parameter in the path means the address cannot be built without a record, and a
 * redirect that names such a route throws („Missing required param“) and leaves the
 * account on a navigation that never completes. The manifest already keeps those routes
 * out; this is the second lock, and it is the one that holds when somebody adds a detail
 * route to the list above by mistake. It costs one `includes` per candidate.
 */
export const isLandablePath = (path: string): boolean => !path.includes(':')

/**
 * The first screen of the menu this account may actually open, or nothing.
 *
 * Nothing is a real answer: an account with no group right at all has nowhere to be sent,
 * and the caller turns that into „Brez dostopa“ — a sentence — rather than a board it
 * would be refused.
 *
 * `holds` answers „does this account hold this key“ and `pathOf` answers „what address is
 * this route name registered at, if it is registered at all“. Both are passed in so that
 * this stays a pure function of its inputs: the router hands it the real router, and the
 * test hands it the real router too, which is the point.
 *
 * A name `pathOf` does not know is skipped rather than returned. A manifest entry naming
 * a route that does not exist is a bug the test fails on, but the console still has to
 * pick a screen for somebody in front of it rather than push them at a name nothing
 * answers to.
 */
export const landingEntryName = (
    holds:  (key: string) => boolean,
    pathOf: (name: string) => string | undefined,
): string => {
    const entry = NAV_ENTRIES.find(candidate => {
        if (!mayOpenGate(candidate, holds)) return false

        const path = pathOf(candidate.name)

        return path !== undefined && isLandablePath(path)
    })

    return entry?.name ?? ''
}
